import React from "react";
import { formatDistanceToNowStrict } from "date-fns";
import { Box, Tooltip } from "@mui/material";
import { styled } from "@mui/styles";
import {
  colors,
  fontSize,
  fontWeight,
  padding,
  borderRadius,
  gaps,
} from "theme/defaultStyle";
import { PolicyManagementRecord } from "entities/UIModel/PolicyManagementRecord";
import { formatDate } from "./policyColumnHelpers";

const TooltipHeading = styled(Box)({
  fontSize: fontSize.small1,
  fontWeight: fontWeight.bold1,
  textTransform: "uppercase",
  marginBottom: gaps.xsmall1,
});

const TooltipLine = styled(Box)({
  fontSize: fontSize.small,
  fontWeight: fontWeight.normal1,
  whiteSpace: "normal",
});

interface BadgeTooltipProps {
  heading: string;
  lines: { label: string; value?: string | null }[];
}

const BadgeTooltip = ({ heading, lines }: BadgeTooltipProps) => (
  <Box>
    <TooltipHeading>{heading}</TooltipHeading>
    {lines
      .filter((line) => Boolean(line.value?.trim()))
      .map((line) => (
        <TooltipLine key={line.label}>
          {line.label}: {line.value}
        </TooltipLine>
      ))}
  </Box>
);

// Shared tooltip behavior for every flag badge.
const tooltipProps = {
  arrow: true,
  placement: "top" as const,
};

const Row = styled(Box)({
  display: "inline-flex",
  alignItems: "center",
  gap: gaps.xsmall1,
  marginLeft: gaps.xsmall1,
});

const Badge = styled(Box)({
  display: "inline-flex",
  alignItems: "center",
  fontSize: fontSize.xxsmall,
  fontWeight: fontWeight.bold2,
  padding: `1px ${padding.xsmall1}`,
  borderRadius: borderRadius.xsmall1,
  textTransform: "uppercase",
  letterSpacing: "0.4px",
  lineHeight: 1.4,
  border: "1px solid transparent",
  cursor: "default",
  userSelect: "none",
  "&.hold": {
    backgroundColor: colors.white,
    color: colors.yellow,
    borderColor: colors.yellow,
  },
  "&.fp": {
    backgroundColor: colors.blue08,
    color: colors.blue02,
    borderColor: colors.blue01,
  },
  "&.locked": {
    backgroundColor: colors.grey10,
    color: colors.grey08,
    borderColor: colors.grey09,
  },
  "&.edited": {
    backgroundColor: colors.white,
    color: colors.maroon01,
    borderColor: colors.maroon01,
  },
  "&.adj": {
    backgroundColor: colors.white,
    color: colors.red01,
    borderColor: colors.red01,
  },
});

const formatBadgeDate = (value?: string | null): string => {
  if (!value) return "";
  const isoDate = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (isoDate) {
    const [, yyyy, mm, dd] = isoDate;
    return `${mm}/${dd}/${yyyy}`;
  }
  return formatDate(value);
};

// Elapsed time since an ISO timestamp.
// Falls back to the formatted date when the value is missing or unparseable.
const formatElapsed = (value?: string | null): string => {
  if (!value) return "";
  const parsed = new Date(value);
  if (isNaN(parsed.getTime())) return formatBadgeDate(value);
  if (parsed.getTime() > Date.now()) return "just now";
  return `${formatDistanceToNowStrict(parsed)} ago`;
};

interface FlagBadgesProps {
  record: Pick<
    PolicyManagementRecord,
    | "IsOnHold"
    | "IsForcePromoted"
    | "isLocked"
    | "IsEdited"
    | "RecordTypeCode"
    | "OnHoldReason"
    | "LastHeldByUserName"
    | "LastOnHoldDate"
    | "Reason"
    | "AdjustmentKind"
    | "ForcePromotedReason"
    | "ForcePromotedByUserName"
    | "ForcePromotedDate"
    | "EditedByUserName"
    | "EditedUpdateDate"
    | "LockedByUserName"
    | "LockedDate"
    | "LockContextActive"
    | "holdInfo"
    | "fpInfo"
    | "adjInfo"
    | "lockedInfo"
    | "editedInfo"
  >;
}

const FlagBadges = ({ record }: FlagBadgesProps) => {
  const badges: React.ReactNode[] = [];

  const holdReason = record.holdInfo?.OnHoldReason ?? record.OnHoldReason;
  const holdBy = record.holdInfo?.LastHeldByUserName ?? record.LastHeldByUserName;
  const holdSince = record.holdInfo?.LastOnHoldDate ?? record.LastOnHoldDate;
  const adjReason = record.adjInfo?.Reason ?? record.Reason;
  const adjKind = record.adjInfo?.AdjustmentKind ?? record.AdjustmentKind;
  const promotReason = record.fpInfo?.ForcePromotedReason ?? record.ForcePromotedReason;
  const promotBy = record.fpInfo?.ForcePromotedByUserName ?? record.ForcePromotedByUserName;
  const promotOn = record.fpInfo?.ForcePromotedDate ?? record.ForcePromotedDate;
  const lockedBy = record.lockedInfo?.LockedByUserName ?? record.LockedByUserName;
  const lockSince = record.lockedInfo?.LockedDate ?? record.LockedDate;
  const lockContext = record.lockedInfo?.LockContextActive ?? record.LockContextActive;
  const EditedBy = record.editedInfo?.EditedByUserName ?? record.EditedByUserName;
  const EditedUpdate = record.editedInfo?.EditedUpdateDate ?? record.EditedUpdateDate;

  if (record.IsOnHold) {
    badges.push(
      <Tooltip
        key="hold"
        {...tooltipProps}
        title={
          <BadgeTooltip
            heading="Hold"
            lines={[
              { label: "Reason", value: holdReason },
              { label: "By", value: holdBy },
              { label: "Since", value: formatBadgeDate(holdSince) },
            ]}
          />
        }
      >
        <Badge className="hold">Hold</Badge>
      </Tooltip>
    );
  }

  if (record.IsForcePromoted) {
    badges.push(
      <Tooltip
        key="fp"
        {...tooltipProps}
        title={
          <BadgeTooltip 
            heading="Force Promoted" 
            lines={[
              { label: "Reason", value: promotReason },
              { label: "By", value: promotBy },
              { label: "On", value: formatBadgeDate(promotOn) },
            ]}
          />
        }
      >
        <Badge className="fp">Force Promoted</Badge>
      </Tooltip>
    );
  }

  if (record.isLocked) {
    badges.push(
      <Tooltip
        key="locked"
        {...tooltipProps}
        title={
          <BadgeTooltip 
            heading="Locked" 
            lines={[
              { label: "Locked by", value: lockedBy },
              { label: "Reason", value: lockContext },
              { label: "Locked Since", value: formatElapsed(lockSince) },
            ]} />}
      >
        <Badge className="locked">Locked</Badge>
      </Tooltip>
    );
  }

  if (record.IsEdited) {
    badges.push(
      <Tooltip
        key="edited"
        {...tooltipProps}
        title={
          <BadgeTooltip 
            heading="Edited" 
            lines={[
              { label: "Edited by", value: EditedBy },
              { label: "Last update", value: formatBadgeDate(EditedUpdate) },
            ]} />}
      >
        <Badge className="edited">Edited</Badge>
      </Tooltip>
    );
  }

  if (record.RecordTypeCode === "A") {
    badges.push(
      <Tooltip
        key="adj"
        {...tooltipProps}
        title={
          <BadgeTooltip
            heading="Adjustment"
            lines={[
              { label: "Kind", value: adjKind },
              { label: "Note", value: adjReason },
            ]}
          />
        }
      >
        <Badge className="adj">ADJ</Badge>
      </Tooltip>
    );
  }

  if (badges.length === 0) return null;

  return <Row>{badges}</Row>;
};

export default FlagBadges;
